// @ts-nocheck

/**
 * MSI3 Clinical Contact Service
 * 
 * Records clinical contacts when providers access patient CMR via ChartMind.
 * Clinical contacts are used to:
 * - Reset inactivity timers for revenue share protection
 * - Determine revenue share transfers during 90-day lookback
 */

import { db } from '../firebase';
import firebase from 'firebase/compat/app';
import { MSI3_CONSTANTS } from './constants';

const { REVENUE_STATUS } = MSI3_CONSTANTS;

export const clinicalContactService = {
  /**
   * Record a clinical contact when provider scans patient QR in ChartMind
   * @param {Object} contactData - Contact details
   * @returns {Promise<Object>} Contact record
   */
  async recordClinicalContact(contactData) {
    const {
      patientUserId,
      facilityUserId, // The facility user who owns the provider
      providerId = null, // Optional: specific provider ID
      contactType, // 'chartmind_cmr_scan', 'chartmind_chart_generated', etc.
      chartmindEncounterId = null, // Link to encounter
      patientDataAccessed = [], // ['name', 'dob'] for Phase 1
    } = contactData;

    // Create contact record
    const contactRef = db.collection('msi3ClinicalContacts').doc();
    const contact = {
      id: contactRef.id,
      patientUserId,
      facilityUserId,
      providerId,
      contactDate: firebase.firestore.FieldValue.serverTimestamp(),
      contactType,
      chartmindEncounterId,
      patientDataAccessed,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    };

    await contactRef.set(contact);

    // Update enrollment record if this facility has revenue share
    await this.updateEnrollmentLastContact(patientUserId, facilityUserId);

    return contact;
  },

  /**
   * Update enrollment's last clinical contact date
   */
  async updateEnrollmentLastContact(patientUserId, facilityUserId) {
    // Get facility partner record to find facility ID
    const facilityPartnerSnapshot = await db
      .collection('msi3FacilityPartners')
      .where('facilityUserId', '==', facilityUserId)
      .limit(1)
      .get();

    if (facilityPartnerSnapshot.empty) {
      return;
    }

    const facilityPartnerId = facilityPartnerSnapshot.docs[0].id;

    // Find patient's enrollment record
    const enrollmentSnapshot = await db
      .collection('msi3PatientEnrollments')
      .where('patientUserId', '==', patientUserId)
      .limit(1)
      .get();

    if (enrollmentSnapshot.empty) {
      return;
    }

    const enrollment = { id: enrollmentSnapshot.docs[0].id, ...enrollmentSnapshot.docs[0].data() };

    // Update last contact date if this facility currently has revenue share
    if (enrollment.currentRevenueFacilityId === facilityPartnerId) {
      await db.collection('msi3PatientEnrollments').doc(enrollment.id).update({
        lastClinicalContactDate: firebase.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    }

    // If patient's revenue share is paused and this is their original facility, restart it
    if (
      enrollment.enrollingFacilityId === facilityPartnerId &&
      enrollment.revenueShareStatus === REVENUE_STATUS.PAUSED &&
      enrollment.subscriptionTier !== 'free'
    ) {
      await db.collection('msi3PatientEnrollments').doc(enrollment.id).update({
        revenueShareStatus: REVENUE_STATUS.ACTIVE,
        currentRevenueFacilityId: facilityPartnerId,
        lastClinicalContactDate: firebase.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    }
  },

  /**
   * Get clinical contact history for a patient
   * @param {string} patientUserId - Patient user ID
   * @param {number} limit - Max records to return
   * @returns {Promise<Array>} Contact records
   */
  async getPatientContacts(patientUserId, limit = 50) {
    const snapshot = await db
      .collection('msi3ClinicalContacts')
      .where('patientUserId', '==', patientUserId)
      .orderBy('contactDate', 'desc')
      .limit(limit)
      .get();

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      contactDate: doc.data().contactDate?.toDate(),
    }));
  },

  /**
   * Get clinical contacts for a facility within a date range
   * Used for 90-day lookback calculations
   * @param {string} facilityUserId - Facility user ID
   * @param {Date} startDate - Start of range
   * @param {Date} endDate - End of range
   * @returns {Promise<Array>} Contact records
   */
  async getFacilityContactsInRange(facilityUserId, startDate, endDate) {
    const snapshot = await db
      .collection('msi3ClinicalContacts')
      .where('facilityUserId', '==', facilityUserId)
      .where('contactDate', '>=', startDate)
      .where('contactDate', '<=', endDate)
      .orderBy('contactDate', 'desc')
      .get();

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      contactDate: doc.data().contactDate?.toDate(),
    }));
  },

  /**
   * Check if a facility had contact with a patient during 90-day lookback
   * @param {string} patientUserId - Patient user ID
   * @param {string} facilityUserId - Facility user ID
   * @param {Date} lookbackStartDate - Start of lookback window
   * @param {Date} lookbackEndDate - End of lookback window
   * @returns {Promise<Object|null>} Most recent contact if found
   */
  async checkLookbackContact(patientUserId, facilityUserId, lookbackStartDate, lookbackEndDate) {
    const snapshot = await db
      .collection('msi3ClinicalContacts')
      .where('patientUserId', '==', patientUserId)
      .where('facilityUserId', '==', facilityUserId)
      .where('contactDate', '>=', lookbackStartDate)
      .where('contactDate', '<=', lookbackEndDate)
      .orderBy('contactDate', 'desc')
      .limit(1)
      .get();

    if (snapshot.empty) return null;

    return {
      id: snapshot.docs[0].id,
      ...snapshot.docs[0].data(),
      contactDate: snapshot.docs[0].data().contactDate?.toDate(),
    };
  },

  /**
   * Get contact statistics for a facility
   * @param {string} facilityUserId - Facility user ID
   * @param {number} daysBack - Number of days to look back
   * @returns {Promise<Object>} Contact statistics
   */
  async getFacilityContactStats(facilityUserId, daysBack = 30) {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - daysBack);

    const contacts = await this.getFacilityContactsInRange(facilityUserId, startDate, new Date());

    // Count unique patients
    const uniquePatients = new Set(contacts.map((c) => c.patientUserId));

    // Count by contact type
    const byType = contacts.reduce((acc, c) => {
      acc[c.contactType] = (acc[c.contactType] || 0) + 1;
      return acc;
    }, {});

    return {
      totalContacts: contacts.length,
      uniquePatients: uniquePatients.size,
      byContactType: byType,
      period: {
        startDate,
        endDate: new Date(),
        daysBack,
      },
    };
  },
};

